"use client";

import { useState } from "react";
import { Globe2 } from "lucide-react";
import { useTranslations } from "@/hooks/use-translations";
import { useI18n } from "@/components/providers/i18n-provider";
import { type Locale } from "@/i18n/config";
import { getApiUrl } from "@/lib/api/utils";
import { getLanguageFlag, languageNames, sortedLocales } from "@/lib/i18n/language-options";
import { cn } from "@/lib/utils";

type CompactLanguageSelectorProps = {
  className?: string;
  /**
   * "pill" is the header control with a globe, the language name and the flag.
   * "icon" is the round 40px button used in the mobile app header, where the
   * neighbouring controls are single-glyph circles. It shows the flag on its
   * own: a globe and a flag together overflow a circle that size.
   */
  variant?: "pill" | "icon";
};

/** A compact header control that works for anonymous visitors and signed-in members. */
export function CompactLanguageSelector({ className, variant = "pill" }: CompactLanguageSelectorProps) {
  const { t } = useTranslations("settings");
  const { locale, setLocale } = useI18n();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleChange = async (nextLocale: Locale) => {
    if (nextLocale === locale || isUpdating) return;

    setIsUpdating(true);
    try {
      await setLocale(nextLocale);

      /* Imported at the moment of use, not at the top of the file.
         This control is in the website header, and a static import of the profile store pulls
         in the profile API client, the authenticated fetch wrapper and the Supabase browser
         client behind it: 230KB of authentication library on a page with nobody signed in.
         Somebody actually changing language can afford to wait for it. */
      const { useProfileStore } = await import("@/stores/profile-store");
      const currentProfile = useProfileStore.getState().currentProfile;
      if (currentProfile) {
        useProfileStore.setState({
          currentProfile: {
            ...currentProfile,
            preferred_language: nextLocale,
          },
        });
      }

      // The cookie and local storage make the choice immediate. Persist it to the member profile
      // as well when this control is used inside the authenticated app shell.
      void fetch(getApiUrl("/api/user/update-language"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language: nextLocale }),
      }).catch(() => {
        // Anonymous landing-page visitors have no profile to update. Their local choice remains valid.
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <label
      className={cn(
        "group relative flex items-center overflow-hidden whitespace-nowrap rounded-full border border-neutral-200/80 bg-white/80 font-semibold text-neutral-700 shadow-sm transition-colors focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15 hover:border-neutral-300",
        variant === "icon"
          ? "h-10 w-10 shrink-0 justify-center text-lg leading-none"
          : "h-9 gap-2 px-2.5 text-sm sm:px-3",
        isUpdating && "opacity-60",
        className,
      )}
      aria-label={t("language")}
    >
      {/* The locale resolves from localStorage and the device, which the server cannot
          see, so the server renders the default and the client may know better. That is
          expected, not a bug: suppress the warning on exactly these text nodes so React
          patches the words instead of regenerating the whole tree from the root. */}
      {variant === "icon" ? (
        <span className="leading-none" aria-hidden="true" suppressHydrationWarning>{getLanguageFlag(locale)}</span>
      ) : (
        <>
          <Globe2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="hidden max-w-[7rem] truncate xl:inline" aria-hidden="true" suppressHydrationWarning>
            {languageNames[locale]}
          </span>
          <span className="inline xl:hidden" aria-hidden="true" suppressHydrationWarning>{getLanguageFlag(locale)}</span>
        </>
      )}
      <select
        value={locale}
        disabled={isUpdating}
        onChange={(event) => void handleChange(event.target.value as Locale)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-wait"
        aria-label={t("language")}
      >
        {sortedLocales.map((option) => (
          <option key={option} value={option}>
            {getLanguageFlag(option)} {languageNames[option]}
          </option>
        ))}
      </select>
    </label>
  );
}
